import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { RichTextService } from '../richtext/richtext.service';

import { EDITORSETTINGS } from './froala.config';
@Component({
  selector: 'app-froala-editor',
  templateUrl: './froala-editor.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => FroalaEditorComponent)
    },
    RichTextService
  ],
  styleUrls: ['./froala-editor.component.scss']
})
export class FroalaEditorComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() height: number = 600;
  @Output() readonly focusEvent: EventEmitter<FocusEvent | Event> = new EventEmitter<FocusEvent | Event>();
  disabled: boolean;
  editor: any;
  _textValue: string;
  showEditor = signal(true);

  private resources: string[] = [];
  froalaOptions: any = {
    // TODO: add more config
    ...EDITORSETTINGS,
    events: {
      focus: (e: any) => this.focusEvent.emit(e)
    }
  };

  constructor(
    private readonly messageService: MessageService,
    private readonly richTextService: RichTextService,
    private readonly translateService: TranslateService
  ) {}
  protected initEditor(e: any) {
    e.initialize();
    setTimeout(() => {
      this.editor = e.getEditor();
    }, 100);
  }
  ngOnInit() {
    this.froalaOptions = {
      ...this.froalaOptions,
      events: {
        ...this.froalaOptions.events,
        contentChanged: () => {
          this.editor?.$oel.trigger('contentChanged');

          // Change layout of table in html-element
          this.editor?.$el.find('table').each(function (_, table: HTMLTableElement) {
            const trs = document.getElementsByTagName('tr');
            const tds = document.getElementsByTagName('td');
            const ths = document.getElementsByTagName('th');

            table.style.setProperty('word-break', 'break-word');

            for (let i = 0; i < trs.length; i++) {
              trs[i].style.setProperty('background', '');
            }
            if (table.classList.contains('fr-no-borders')) {
              table.style.setProperty('border', '0px');
              table.style.setProperty('border-style', 'none');
              for (let i = 0; i < tds.length; i++) {
                tds[i].style.setProperty('border-style', 'none');
              }
              for (let i = 0; i < ths.length; i++) {
                ths[i].style.setProperty('border-style', 'none');
              }
            }

            if (table.classList.contains('fr-dashed-borders')) {
              for (let i = 0; i < tds.length; i++) {
                tds[i].style.setProperty('border-style', 'dashed');
              }
              for (let i = 0; i < ths.length; i++) {
                ths[i].style.setProperty('border-style', 'dashed');
              }
            }

            if (table.classList.contains('fr-alternate-rows')) {
              for (let i = 0; i < trs.length; i++) {
                if (i % 2 !== 0) {
                  trs[i].style.setProperty('background', 'whitesmoke');
                }
              }
            }
          });
        },
        init: () => {
          this.editor?.$el.find('table').each(function (_, table: HTMLTableElement) {
            table.style.setProperty('border', '1px');
          });
        },
        'image.beforeUpload': (images: File[]) => {
          if (images.length) {
            [...images].forEach((image) => {
              if (image.size > this.froalaOptions.imageMaxSize) {
                this.messageService.add({
                  severity: 'error',
                  summary: 'image.size > this.froalaOptions.imageMaxSize'
                });
              } else {
                const formData = new FormData();
                formData.append('file', image);

                fetch(`${environment.BACKEND_URL}/htmlimage/upload`, {
                  method: 'POST',
                  headers: {
                    Authorization: `Bearer ${environment.BEARER_TOKEN}`
                  },
                  body: formData
                })
                  .then((response) => response.json())
                  .then(({ link }) => {
                    const bloblink = URL.createObjectURL(image);
                    this.editor.image.insert(
                      bloblink,
                      null,
                      {
                        'data-src': link
                      },
                      this.editor.image.get()
                    );
                    this.editor.popups.hideAll();
                  })
                  .catch((error) => {
                    console.error('Error uploading image:', error);
                  });
              }
            });
            return false;
          }
          return false;
        },
        'image.beforePasteUpload': (img: HTMLImageElement) => {
          const src = img.src;

          if (
            (src.startsWith('data:image/') || src.startsWith('blob:') || src.startsWith('file://')) &&
            !img.hasAttribute('data-src') &&
            !img.hasAttribute('data-processing')
          ) {
            img.setAttribute('data-processing', 'true');

            if (src.startsWith('data:image/')) {
              const blob = this.dataURLtoBlob(src);
              const file = new File([blob], 'pasted-image.png', { type: blob.type });
              this.uploadImageFromPaste(file, img);
            } else if (src.startsWith('blob:')) {
              fetch(src)
                .then((response) => response.blob())
                .then((blob) => {
                  const file = new File([blob], 'pasted-image.png', { type: blob.type });
                  this.uploadImageFromPaste(file, img);
                })
                .catch((error) => {
                  console.error('Error fetching blob:', error);
                });
            }
          }

          img.remove();
          return false;
        },
        'image.error': (error: any) => {
          this.messageService.add({
            severity: 'error',
            summary: 'image.error',
            detail: error.message
          });
        },
        'paste.after': () => {
          // Handle tables
          this.editor?.$el.find('table').each(function (_, table: HTMLTableElement) {
            table.style.removeProperty('width');
            table.style.width = '100%';
            table.style.setProperty('border', '1px solid');
          });
        }
      },
      language: this.translateService.currentLang
    };
  }
  ngOnDestroy(): void {
    this.resources.forEach((resource) => URL.revokeObjectURL(resource));
  }
  onChange: any = () => {};
  onTouched: any = () => {};
  get textValue(): string {
    return this._textValue;
  }
  set textValue(val: string) {
    this._textValue = val;
    this.onChange(this.richTextService.transformToParsedRichText(val));
    this.onTouched();
  }
  registerOnChange(fn) {
    this.onChange = fn;
  }
  registerOnTouched(fn) {
    this.onTouched = fn;
  }
  writeValue(value) {
    if (value) {
      this.richTextService
        .transformToRichText(value)
        .pipe(take(1))
        .subscribe((richText) => {
          this.textValue = richText;
        });
    } else {
      this.textValue = value;
    }
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.froalaDisable(isDisabled);
  }
  private froalaDisable(isDisabled: boolean) {
    this.showEditor.set(false);
    setTimeout(() => {
      this.froalaOptions = {
        ...this.froalaOptions,
        events: {
          ...this.froalaOptions?.events,
          initialized: function () {
            if (isDisabled) {
              this.edit.off();
            } else {
              this.edit.on();
            }
          }
        }
      };
      this.showEditor.set(true);
    });
  }

  private dataURLtoBlob(dataURL: string): Blob {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  private uploadImageFromPaste(file: File, imgElement: HTMLImageElement): void {
    const formData = new FormData();
    formData.append('file', file);

    fetch(`${environment.BACKEND_URL}/htmlimage/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${environment.BEARER_TOKEN}`
      },
      body: formData
    })
      .then((response) => {
        if (!response.ok) {
          return response.text().then((text) => {
            throw new Error(`HTTP ${response.status}: ${text}`);
          });
        }
        return response.json();
      })
      .then(({ link }) => {
        imgElement.setAttribute('data-src', link);
        imgElement.removeAttribute('data-processing');

        const bloblink = URL.createObjectURL(file);
        this.editor?.image.insert(
          bloblink,
          null,
          {
            'data-src': link
          },
          this.editor?.image.get()
        );
        this.editor?.popups.hideAll();
      })
      .catch((error) => {
        console.error('Error uploading pasted image:', error);
        imgElement.remove();
      });
  }
}
